import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button, Dropdown, Field, Input, MessageBar, Option, Spinner, Textarea, Title3,
} from "@fluentui/react-components";
import { useCategories, useCreateTicket } from "../../api/hooks";
import type { Priority } from "../../types/models";

const PRIORITIES: Priority[] = ["Low", "Medium", "High", "Critical"];

export function NewTicketPage() {
  const cats = useCategories();
  const create = useCreateTicket();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState<string>("");
  const [priority, setPriority] = useState<Priority>("Medium");
  const [department, setDepartment] = useState("");
  const [location, setLocation] = useState("");
  const [deviceDetails, setDeviceDetails] = useState("");

  const valid = title.trim().length >= 3 && description.trim().length >= 3 && categoryId;

  async function submit() {
    const ticket = await create.mutateAsync({
      title, description, categoryId, priority,
      department: department || undefined,
      location: location || undefined,
      deviceDetails: deviceDetails || undefined,
    });
    navigate(`/tickets/${ticket.id}`);
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <Title3>New ticket</Title3>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
        <Field label="Title" required>
          <Input value={title} onChange={(_, d) => setTitle(d.value)} placeholder="Short summary of the issue" />
        </Field>
        <Field label="Description" required hint="Steps to reproduce, error messages, business impact.">
          <Textarea rows={6} value={description} onChange={(_, d) => setDescription(d.value)} />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Category" required>
            <Dropdown
              placeholder={cats.isLoading ? "Loading..." : "Select category"}
              selectedOptions={categoryId ? [categoryId] : []}
              value={cats.data?.find((c) => c.id === categoryId)?.name ?? ""}
              onOptionSelect={(_, d) => setCategoryId(d.optionValue ?? "")}
            >
              {(cats.data ?? []).map((c) => <Option key={c.id} value={c.id}>{c.name}</Option>)}
            </Dropdown>
          </Field>
          <Field label="Priority">
            <Dropdown
              value={priority}
              selectedOptions={[priority]}
              onOptionSelect={(_, d) => setPriority((d.optionValue as Priority) ?? "Medium")}
            >
              {PRIORITIES.map((p) => <Option key={p} value={p}>{p}</Option>)}
            </Dropdown>
          </Field>
          <Field label="Department">
            <Input value={department} onChange={(_, d) => setDepartment(d.value)} />
          </Field>
          <Field label="Location">
            <Input value={location} onChange={(_, d) => setLocation(d.value)} />
          </Field>
        </div>
        <Field label="Device details" hint="Make, model, OS, asset tag (optional)">
          <Input value={deviceDetails} onChange={(_, d) => setDeviceDetails(d.value)} />
        </Field>
        {create.isError && <MessageBar intent="error">{(create.error as Error).message}</MessageBar>}
        <div style={{ display: "flex", gap: 8 }}>
          <Button appearance="primary" disabled={!valid || create.isPending} onClick={submit}>
            {create.isPending ? <Spinner size="tiny" /> : "Submit ticket"}
          </Button>
          <Button appearance="secondary" onClick={() => navigate("/my-tickets")}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}
